import qs from "qs";
import api from "src/shared/api";
import { EQueryTagTypes, ISwoolResponse } from "src/shared/types/api";
import { getProvidesTags } from "src/shared/utils/url/query.helpers";
import {
  IDraftReportItem,
  IExportDraftReportQuery,
  IGetDraftReportQuery,
  IUpdateDraftReportItemDTO,
} from "../types";

const draftReportApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDraftReportsList: build.query<
      ISwoolResponse<IDraftReportItem[]>,
      Partial<IGetDraftReportQuery>
    >({
      query: (params) => {
        const queryString = qs.stringify(params, {
          arrayFormat: "indices",
          skipNulls: true,
        });

        const qsPart = queryString ? `?${queryString}` : "";

        return {
          url: `api/reports/partner/bonuses/preliminary/${qsPart}`,
          method: "GET",
        };
      },
      providesTags: (response) => {
        const tags = [{ type: EQueryTagTypes.DRAFT_REPORT, id: "LIST" }];
        if (!response) {
          return tags;
        }
        tags.push(
          ...getProvidesTags<IDraftReportItem>(
            response.data,
            EQueryTagTypes.DRAFT_REPORT,
          ),
        );

        return tags;
      },
    }),
    calculateBonuses: build.mutation<void, void>({
      query: () => ({
        url: `/api/reports/partner/bonuses/preliminary/calculate`,
        method: "POST",
      }),
      invalidatesTags: () => [
        { type: EQueryTagTypes.DRAFT_REPORT, id: "LIST" },
      ],
    }),
    saveBonuses: build.mutation<void, void>({
      query: () => ({
        url: `/api/reports/partner/bonuses/preliminary/save`,
        method: "POST",
      }),
      invalidatesTags: () => [
        { type: EQueryTagTypes.DRAFT_REPORT, id: "LIST" },
      ],
    }),
    updateBonusItem: build.mutation<
      ISwoolResponse<IDraftReportItem>,
      IUpdateDraftReportItemDTO
    >({
      query: ({ id, spends }) => ({
        url: `/api/reports/partner/bonuses/preliminary/update`,
        method: "POST",
        body: { adjustments: [{ id, spends }] },
      }),
      invalidatesTags: (response) => {
        if (response?.data.id)
          return [{ type: EQueryTagTypes.DRAFT_REPORT, id: response?.data.id }];

        return [];
      },
    }),
    updateBonusItems: build.mutation<
      ISwoolResponse<IDraftReportItem>,
      IUpdateDraftReportItemDTO[]
    >({
      query: (data) => ({
        url: `/api/reports/partner/bonuses/preliminary/update`,
        method: "POST",
        body: { adjustments: data },
      }),
      invalidatesTags: () => [
        { type: EQueryTagTypes.DRAFT_REPORT, id: "LIST" },
      ],
    }),
    exportDraftReport: build.query<string, Partial<IExportDraftReportQuery>>({
      query: (params) => {
        return {
          url: "api/reports/partner/bonuses/preliminary/export",
          method: "GET",
          params,
          responseHandler: (response) => response.text(),
        };
      },
      transformResponse: (response: string) => response,
    }),
  }),
});

export const {
  useGetDraftReportsListQuery,
  useCalculateBonusesMutation,
  useSaveBonusesMutation,
  useUpdateBonusItemMutation,
  useUpdateBonusItemsMutation,
  useLazyExportDraftReportQuery,
} = draftReportApi;
